import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import {
  ClearNotifications,
  InvalidStateError,
  PlannedError,
  ShowErrorNotification,
  ShowInfoNotification,
  ShowSuccessNotification,
  SimpleRouterState,
  TranslationId,
  convertToHttpParams,
  handleErrors,
  tapNextOrError
} from '@platform-frontend/util';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take, tap, timeout } from 'rxjs/operators';
import { ACCOUNT_ERROR_HANDLERS } from '../account-error-handlers';
import { AccountRouteService } from '../account-router';
import { SetAuthenticationContext } from '../auth-context';
import { SetAuthFormEmail } from '../auth-form';
import { LinkAccountState } from '../link-account';
import { LINK_ACCOUNT_ERROR_HANDLERS } from '../link-account/link-account-error-handlers';
import {
  MSG_REQUEST_PASSWORD_RESET_DEFAULT,
  MSG_REQUEST_PASSWORD_RESET_DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS,
  MSG_REQUEST_PASSWORD_RESET_MAX_MAIL_REQUESTS_EXCEEDED,
  MSG_REQUEST_PASSWORD_RESET_USER_NOT_KNOWN,
  MSG_RESEND_PASSWORD_UPDATE_MAIL_DEFAULT,
  MSG_RESEND_PASSWORD_UPDATE_MAIL_MAX_MAIL_REQUESTS_EXCEEDED,
  MSG_RESEND_PASSWORD_UPDATE_MAIL_SERVER_UNREACHABLE,
  MSG_RESEND_PASSWORD_UPDATE_MAIL_SUCCESS,
  MSG_RESET_PASSWORD_ALREADY_USED,
  MSG_RESET_PASSWORD_DEFAULT,
  MSG_RESET_PASSWORD_RESTORE_ID_EXPIRED,
  MSG_RESET_PASSWORD_RESTORE_ID_NOT_MATCHING_EMAIL,
  MSG_RESET_PASSWORD_RESTORE_ID_NOT_MATCHING_VERIFICATION_REF,
  MSG_RESET_PASSWORD_SUCCESS,
  MSG_RESET_PASSWORD_USER_NOT_KNOWN,
  MSG_SET_PASSWORD_ALREADY_USED,
  MSG_SET_PASSWORD_DEFAULT,
  MSG_SET_PASSWORD_RESTORE_ID_EXPIRED,
  MSG_SET_PASSWORD_RESTORE_ID_NOT_MATCHING_EMAIL,
  MSG_SET_PASSWORD_RESTORE_ID_NOT_MATCHING_VERIFICATION_REF,
  MSG_SET_PASSWORD_SUCCESS,
  MSG_SET_PASSWORD_USER_NOT_KNOWN,
  MSG_VERIFY_PASSWORD_UPDATE_DEFAULT,
  MSG_VERIFY_PASSWORD_UPDATE_SIGNED_IN_WITH_ANOTHER_ACCOUNT
} from '../notification-messages';
import { GotoCheckRecoveryEmail, GotoSignIn, Session, SessionState, SetSession, SignOut, SignOutOnError, UpdateSessionFromBrowser } from '../session';
import { PasswordUpdateStateModel } from './model/password-update-state-model';
import { PasswordUpdateVerificationResult } from './model/password-update-verification-result';
import { RequestNewPasswordResponse } from './model/request-new-password-response';
import { UpdatePasswordMode } from './model/update-password-mode';
import {
  CancelPasswordUpdate,
  CompletePasswordUpdate,
  GotoResetPassword,
  GotoSetPassword,
  RequestPasswordUpdate,
  ResendPasswordUpdateMail,
  ResetPassword,
  SetPassword,
  VerifyPasswordUpdate
} from './password-update.actions';

const DEFAULT_STATE: PasswordUpdateStateModel = {
  email: null,
  verificationRef: null,
  verificationStatus: 'UNVERIFIED',
  mode: 'resetPassword'
};

@State<PasswordUpdateStateModel>({
  name: 'passwordUpdate',
  defaults: DEFAULT_STATE
})
@Injectable()
export class PasswordUpdateState {
  constructor(private accountRouteService: AccountRouteService, private http: HttpClient, private store: Store) {}

  private static readonly POLLING_TAG = Symbol('PasswordUpdateState: PollingTag');
  private static readonly RESEND_EMAIL_TAG = Symbol('PasswordUpdateState: ResendEmailTag');

  email$: Observable<string | null> = this.store.select(SessionState.email);
  isSignedIn$: Observable<boolean> = this.store.select(SessionState.isSignedIn);
  linkedAccountId$: Observable<string | null> = this.store.select(LinkAccountState.accountId);

  @Selector()
  static email(state: PasswordUpdateStateModel): string | null {
    return state.email;
  }

  @Selector()
  static verified(state: PasswordUpdateStateModel): boolean {
    return state.verificationStatus !== 'UNVERIFIED';
  }

  @Selector()
  static verificationNeeded(state: PasswordUpdateStateModel): boolean {
    return state.verificationStatus !== 'NOT_REQUIRED';
  }

  @Selector()
  static mode(state: PasswordUpdateStateModel): UpdatePasswordMode {
    return state.mode;
  }

  @Action(ResetPassword)
  resetPassword(ctx: StateContext<PasswordUpdateStateModel>, action: ResetPassword): Observable<void> {
    return this.updatePassword(ctx, action, MSG_RESET_PASSWORD_SUCCESS).pipe(
      handleErrors(ACCOUNT_ERROR_HANDLERS, {
        ALREADY_USED: new ShowErrorNotification(MSG_RESET_PASSWORD_ALREADY_USED),
        USER_NOT_KNOWN: new ShowErrorNotification(MSG_RESET_PASSWORD_USER_NOT_KNOWN),
        RESTORE_ID_NOT_MATCHING_VERIFICATION_REF: new ShowErrorNotification(MSG_RESET_PASSWORD_RESTORE_ID_NOT_MATCHING_VERIFICATION_REF),
        RESTORE_ID_NOT_MATCHING_EMAIL: new ShowErrorNotification(MSG_RESET_PASSWORD_RESTORE_ID_NOT_MATCHING_EMAIL),
        RESTORE_ID_EXPIRED: new ShowErrorNotification(MSG_RESET_PASSWORD_RESTORE_ID_EXPIRED),
        DEFAULT: new ShowErrorNotification(MSG_RESET_PASSWORD_DEFAULT)
      })
    );
  }

  @Action(SetPassword)
  setPassword(ctx: StateContext<PasswordUpdateStateModel>, action: SetPassword): Observable<void> {
    return this.updatePassword(ctx, action, MSG_SET_PASSWORD_SUCCESS).pipe(
      handleErrors(ACCOUNT_ERROR_HANDLERS, {
        ALREADY_USED: new ShowErrorNotification(MSG_SET_PASSWORD_ALREADY_USED),
        USER_NOT_KNOWN: new ShowErrorNotification(MSG_SET_PASSWORD_USER_NOT_KNOWN),
        RESTORE_ID_NOT_MATCHING_VERIFICATION_REF: new ShowErrorNotification(MSG_SET_PASSWORD_RESTORE_ID_NOT_MATCHING_VERIFICATION_REF),
        RESTORE_ID_NOT_MATCHING_EMAIL: new ShowErrorNotification(MSG_SET_PASSWORD_RESTORE_ID_NOT_MATCHING_EMAIL),
        RESTORE_ID_EXPIRED: new ShowErrorNotification(MSG_SET_PASSWORD_RESTORE_ID_EXPIRED),
        DEFAULT: new ShowErrorNotification(MSG_SET_PASSWORD_DEFAULT)
      })
    );
  }

  private updatePassword(
    ctx: StateContext<PasswordUpdateStateModel>,
    action: ResetPassword | SetPassword,
    successNotification: TranslationId
  ): Observable<void> {
    return of(action.updatePasswordModel.restoreId).pipe(
      switchMap(restoreId => (restoreId ? ctx.dispatch([new SignOut('none'), new SetAuthenticationContext('SIGN_IN')]) : of(null))),
      switchMap(() =>
        this.http.post<Session>(PLATFORM_ENVIRONMENT.apiUrl + '/account/setPassword', {
          ...action.updatePasswordModel,
          verificationRef: ctx.getState().verificationRef
        })
      ),
      switchMap(session => ctx.dispatch([new ShowSuccessNotification(successNotification), new SetSession(session)])),
      tap(() => ctx.patchState(DEFAULT_STATE))
    );
  }

  @Action(RequestPasswordUpdate)
  requestPasswordUpdate(ctx: StateContext<PasswordUpdateStateModel>, action: RequestPasswordUpdate): Observable<unknown> {
    return ctx.dispatch(new SetAuthFormEmail(action.email)).pipe(
      switchMap(() => this.linkedAccountId$),
      take(1),
      switchMap(linkedAccountId =>
        this.http.post<RequestNewPasswordResponse>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/requestNewPassword', {
          email: action.email,
          securityUpdate: action.securityUpdate,
          redirect: this.accountRouteService.getRedirectUrl(this.store.selectSnapshot(SimpleRouterState.paramMap)),
          linkedAccountId
        })
      ),
      handleErrors(ACCOUNT_ERROR_HANDLERS, {
        DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS: new ShowErrorNotification(
          MSG_REQUEST_PASSWORD_RESET_DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS(action.email)
        ),
        MAX_MAIL_REQUESTS_EXCEEDED: new ShowErrorNotification(MSG_REQUEST_PASSWORD_RESET_MAX_MAIL_REQUESTS_EXCEEDED),
        USER_NOT_KNOWN: new ShowErrorNotification(MSG_REQUEST_PASSWORD_RESET_USER_NOT_KNOWN),
        DEFAULT: new ShowErrorNotification(MSG_REQUEST_PASSWORD_RESET_DEFAULT),
        ...LINK_ACCOUNT_ERROR_HANDLERS(this.store)
      }),
      switchMap(response => this.handleRequestPasswordUpdateResult(ctx, action.email, response))
    );
  }

  private handleRequestPasswordUpdateResult(
    ctx: StateContext<PasswordUpdateStateModel>,
    email: string,
    response: RequestNewPasswordResponse
  ): Observable<unknown> {
    ctx.patchState({
      email,
      verificationRef: response.verificationRef,
      verificationStatus: response.verificationStatus,
      mode: response.mode
    });

    const queryParams = { email };
    return response.verificationStatus !== 'NOT_REQUIRED'
      ? ctx.dispatch(new GotoCheckRecoveryEmail())
      : response.mode === 'resetPassword'
      ? ctx.dispatch(new GotoResetPassword({ queryParams }))
      : ctx.dispatch(new GotoSetPassword({ queryParams }));
  }

  @Action(VerifyPasswordUpdate)
  verifyPasswordUpdate(ctx: StateContext<PasswordUpdateStateModel>, { timeoutInMilliseconds }: VerifyPasswordUpdate): Observable<unknown> {
    const params = this.getVerificationRef(ctx);
    const nOptions = { keepExisting: true, tags: [PasswordUpdateState.POLLING_TAG] };
    return ctx.dispatch(new UpdateSessionFromBrowser()).pipe(
      switchMap(() => this.email$),
      take(1),
      switchMap(sessionEmail =>
        this.http.get<PasswordUpdateVerificationResult>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/passwordUpdated', {
          params: convertToHttpParams({ ...params, sessionEmail })
        })
      ),
      timeout({ each: timeoutInMilliseconds - 50, with: () => throwError(() => new PlannedError('SERVER_UNREACHABLE')) }),
      tap(({ verificationStatus }) => ctx.patchState({ verificationStatus })),
      tapNextOrError(() => ctx.dispatch(new ClearNotifications(PasswordUpdateState.POLLING_TAG))),
      handleErrors({
        DEFAULT: new ShowErrorNotification(MSG_VERIFY_PASSWORD_UPDATE_DEFAULT, nOptions),
        NOT_FOUND: new GotoSignIn({ signInMode: 'ON_ERROR' }),
        SIGNED_IN_WITH_ANOTHER_ACCOUNT: new SignOutOnError(new ShowErrorNotification(MSG_VERIFY_PASSWORD_UPDATE_SIGNED_IN_WITH_ANOTHER_ACCOUNT))
      })
    );
  }

  @Action(ResendPasswordUpdateMail)
  resendPasswordUpdateMail(ctx: StateContext<PasswordUpdateStateModel>): Observable<void> {
    const nOptions = { keepExisting: true, tags: [PasswordUpdateState.RESEND_EMAIL_TAG] };
    return this.http.post<void>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/resendRequestNewPasswordMail', this.getVerificationRef(ctx)).pipe(
      tapNextOrError(() => ctx.dispatch(new ClearNotifications(PasswordUpdateState.RESEND_EMAIL_TAG))),
      switchMap(() => ctx.dispatch(new ShowInfoNotification(MSG_RESEND_PASSWORD_UPDATE_MAIL_SUCCESS, nOptions))),
      handleErrors(ACCOUNT_ERROR_HANDLERS, {
        MAX_MAIL_REQUESTS_EXCEEDED: new ShowErrorNotification(MSG_RESEND_PASSWORD_UPDATE_MAIL_MAX_MAIL_REQUESTS_EXCEEDED, nOptions),
        SERVER_UNREACHABLE: new ShowErrorNotification(MSG_RESEND_PASSWORD_UPDATE_MAIL_SERVER_UNREACHABLE, nOptions),
        DEFAULT: new ShowErrorNotification(MSG_RESEND_PASSWORD_UPDATE_MAIL_DEFAULT, nOptions)
      })
    );
  }

  @Action(CompletePasswordUpdate)
  completePasswordUpdate(ctx: StateContext<PasswordUpdateStateModel>): Observable<unknown> {
    return this.isSignedIn$.pipe(
      take(1),
      switchMap(isSignedIn =>
        isSignedIn
          ? of(null)
          : this.http
              .post<Session>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/completePasswordUpdate', { ...this.getVerificationRef(ctx) })
              .pipe(switchMap(session => ctx.dispatch(new SetSession(session))))
      ),
      tap(() => ctx.patchState(DEFAULT_STATE))
    );
  }

  @Action(CancelPasswordUpdate)
  cancelSignUp(ctx: StateContext<PasswordUpdateStateModel>): Observable<unknown> {
    return this.http.post<void>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/cancelPasswordUpdate', this.getVerificationRef(ctx)).pipe(
      catchError(() => of(null)),
      tap(() => ctx.patchState(DEFAULT_STATE))
    );
  }

  private getVerificationRef(ctx: StateContext<PasswordUpdateStateModel>): { verificationRef: string } {
    const { verificationRef } = ctx.getState();

    if (verificationRef === null) {
      throw new InvalidStateError('Password update is not initialized.');
    }

    return { verificationRef };
  }
}
